import { createContext, useContext, useState, ReactNode } from 'react';
import { fetchCartCount } from '../Services/AppAction';
import { getData } from '../Services/Util';

interface CartContextType {
  cartCount: number;
  setCartCount: React.Dispatch<React.SetStateAction<number>>;
  addToCart: () => void;
  removeFromCart: () => void;
  clearCart: () => void;
  fetchAndUpdateCartCount: () => Promise<void>; // ✅ added
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartCount, setCartCount] = useState<number>(0);

  const addToCart = () => setCartCount(prev => prev + 1);
  const removeFromCart = () => setCartCount(prev => (prev > 0 ? prev - 1 : 0));
  const clearCart = () => setCartCount(0);

  const fetchAndUpdateCartCount = async () => {
    try {
      const userData = await getData('userData');
      const partyId = userData?.PARTY_ID;
      if (!partyId) {return;}

      const response = await fetchCartCount(partyId);
      const count = response?.data?.[0]?.PRODUCT_CART_COUNT;
      if (response?.status === 0 && count !== undefined) {
        setCartCount(Number(count));
      }
    } catch (err) {
      console.error('Error fetching cart count:', err);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartCount,
        setCartCount,
        addToCart,
        removeFromCart,
        clearCart,
        fetchAndUpdateCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {throw new Error('useCart must be used within a CartProvider');}
  return context;
};
